<template>
  <ResponsiveLayout :progress="progress">
    <template #header_end><HeaderHelp /></template>

    <div
      v-if="showSpinner"
      class="contextual-download-link__spinner"
    >
      <Base64Spinner />
    </div>

    <div
      v-else
      class="contextual-download-link__content kds-main-wrapper"
    >
      <p
        class="contextual-download-link__title kds-typography-title-xlarge"
        data-qa="contextual-download-link-title"
      >
        {{ $t('eu__contextual_download__title') }}
      </p>

      <div class="contextual-download-link__layout">
        <div class="contextual-download-link__main">
          <!-- Program card: mobile=first, desktop=right (229px) -->
          <div
            class="contextual-download-link__card contextual-download-link__card--program"
          >
            <p class="kds-typography-ui-strong-large">
              {{ $t('eu__contextual_download__program_label') }}
            </p>
            <div class="contextual-download-link__program">
              <img
                class="contextual-download-link__pain-icon"
                :src="painAreaIcon"
                alt=""
              />
              <p
                class="contextual-download-link__program-name kds-typography-title-medium"
              >
                {{ $t(programNameKey) }}
              </p>
            </div>
          </div>

          <!-- Next steps card: mobile=second, desktop=left (flex-1) -->
          <div
            class="contextual-download-link__card contextual-download-link__card--steps contextual-download-link__card--blue"
          >
            <p class="kds-typography-ui-strong-large">
              {{ $t('eu__contextual_download__next_steps_heading') }}
            </p>
            <ProgressStepsContainer>
              <ProgressStepsItem
                :state="step1State"
                :todo-icon="progressIncompleteGreen"
                :done-icon="progressCompleteGreen"
              >
                <span class="contextual-download-link__step">
                  {{ $t(step1Key) }}
                </span>
              </ProgressStepsItem>
              <ProgressStepsItem
                state="todo"
                :todo-icon="progressIncompleteGreen"
                :done-icon="progressCompleteGreen"
              >
                <span class="contextual-download-link__step">
                  {{ $t(step2Key) }}
                  <template
                    v-if="variant === VARIANTS.ACTIVATED && !passwordless"
                  >
                    <br />
                    <strong>{{ euStore.formData.email }}</strong>
                  </template>
                </span>
              </ProgressStepsItem>
              <ProgressStepsItem
                state="todo"
                :todo-icon="progressIncompleteGreen"
                :done-icon="progressCompleteGreen"
                :class="{
                  'contextual-download-link__step3-desktop':
                    variant === VARIANTS.ACTIVATED,
                }"
              >
                <span class="contextual-download-link__step">
                  {{ $t(step3Key) }}
                </span>
              </ProgressStepsItem>
            </ProgressStepsContainer>

            <!-- ACTIVATED mobile: store badges inside card (hidden on desktop) -->
            <div
              v-if="variant === VARIANTS.ACTIVATED && state !== STATES.IN_APP"
              class="contextual-download-link__store contextual-download-link__store--mobile-only"
            >
              <StoreBadge
                v-if="showPlayStoreLink"
                class="contextual-download-link__store-item"
                :lang="lang"
                app-type="android"
              />
              <StoreBadge
                v-if="showAppStoreLink"
                class="contextual-download-link__store-item"
                :lang="lang"
                app-type="ios"
              />
            </div>
          </div>
        </div>

        <!-- Passwordless sign-up: the password is set from an email -->
        <div
          v-if="passwordless"
          class="contextual-download-link__card contextual-download-link__card--blue"
          data-qa="set-password"
        >
          <p class="contextual-download-link__step">
            <strong>{{
              $t('eu__contextual_download__set_password_prefix')
            }}</strong>
            {{
              $t('eu__contextual_download__set_password_body', {
                email: euStore.formData.email,
              })
            }}
          </p>
        </div>

        <!-- PRESCRIPTION/DOCTOR mobile: download card (hidden on desktop) -->
        <div
          v-if="variant !== VARIANTS.ACTIVATED && state !== STATES.IN_APP"
          class="contextual-download-link__card contextual-download-link__card--blue contextual-download-link__mobile-download"
        >
          <p class="contextual-download-link__step">
            <strong>{{
              $t('eu__contextual_download__interim_download_prefix')
            }}</strong>
            <br />
            {{ $t('eu__contextual_download__interim_download_body') }}
          </p>
          <div class="contextual-download-link__store">
            <StoreBadge
              v-if="showPlayStoreLink"
              class="contextual-download-link__store-item"
              :lang="lang"
              app-type="android"
            />
            <StoreBadge
              v-if="showAppStoreLink"
              class="contextual-download-link__store-item"
              :lang="lang"
              app-type="ios"
            />
          </div>
        </div>

        <!-- Desktop QR/download banner (hidden on mobile) -->
        <div
          v-if="state !== STATES.IN_APP"
          class="contextual-download-link__card contextual-download-link__card--blue contextual-download-link__qr-banner"
        >
          <p
            class="contextual-download-link__banner-text kds-typography-ui-strong-large"
          >
            <template v-if="variant === VARIANTS.ACTIVATED">
              {{ $t('eu__contextual_download__qr_explanation') }}
            </template>
            <template v-else>
              <strong>{{
                $t('eu__contextual_download__interim_download_prefix')
              }}</strong>
              {{ $t('eu__contextual_download__qr_explanation') }}
            </template>
          </p>
          <img
            class="contextual-download-link__qr-code"
            :src="qrCodeUrl"
            alt="Download Kaia QR Code"
          />
          <div
            class="contextual-download-link__store contextual-download-link__store--column"
          >
            <StoreBadge
              v-if="showPlayStoreLink"
              class="contextual-download-link__store-item"
              :lang="lang"
              app-type="android"
            />
            <StoreBadge
              v-if="showAppStoreLink"
              class="contextual-download-link__store-item"
              :lang="lang"
              app-type="ios"
            />
          </div>
        </div>
      </div>

      <!-- In-app: CTA button instead of badges -->
      <div
        v-if="state === STATES.IN_APP"
        class="contextual-download-link__redirect-cta"
      >
        <BaseButton
          :text="$t('b2b_get_started_btn_text')"
          @click="redirect"
        />
      </div>
    </div>
  </ResponsiveLayout>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue'
import useUserStore from '@shared/stores/useUserStore.js'
import useEuStore from '@eu/app/stores/useEuStore.js'
import useSelfTestStore from '@shared/modules/assessment/stores/useSelfTestStore.js'
import useDoctorOutreachStore from '@eu/app/stores/useDoctorOutreachStore.js'
import { likelyHasAppStore, likelyHasPlayStore } from '@shared/utils'
import { isFromApp } from '@shared/helpers/isFromApp.js'
import getQRCodeUrl from '@shared/helpers/getQRCodeUrl.js'
import useDeepLink, {
  DEEP_LINK_CONFIGS,
} from '@shared/composables/useDeepLink.js'
import useOnboardingProgress from '@eu/app/composables/useOnboardingProgress.js'

import painUpperBack from '@shared/assets/img/pain_upper_back_female.svg'
import painLowerBack from '@shared/assets/img/pain_lower_back_female.svg'
import progressIncompleteGreen from '@shared/assets/img/progress_incomplete_green.svg'
import progressCompleteGreen from '@shared/assets/img/progress_complete_green.svg'

import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import Base64Spinner from '@shared/components/Base64Spinner.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import StoreBadge from '@shared/components/StoreBadge.vue'
import ProgressStepsContainer from '@shared/components/ProgressStepsContainer.vue'
import ProgressStepsItem from '@shared/components/ProgressStepsItem.vue'

const userStore = useUserStore()
const euStore = useEuStore()
const selfTestStore = useSelfTestStore()
const doctorOutreachStore = useDoctorOutreachStore()
const { progress } = useOnboardingProgress()
const { redirect, ensureDeepLink } = useDeepLink(DEEP_LINK_CONFIGS.authToken)

const STATES = { IN_APP: 'in_app', BROWSER: 'browser' }
const VARIANTS = {
  ACTIVATED: 'activated',
  DOCTOR_OUTREACH: 'doctor_outreach',
  PRESCRIPTION_SUBMITTED: 'prescription_submitted',
}

const state = isFromApp() ? STATES.IN_APP : STATES.BROWSER

const variant = (() => {
  if (userStore.user?.hasActiveSubscription) return VARIANTS.ACTIVATED
  if (doctorOutreachStore.prescriberRequest != null)
    return VARIANTS.DOCTOR_OUTREACH
  return VARIANTS.PRESCRIPTION_SUBMITTED
})()

const showSpinner = ref(false)
const lang = computed(() => userStore.lang)
const qrCodeUrl = computed(() => getQRCodeUrl(lang.value))

// Only the activated screen has passwordless copy
const passwordless =
  variant === VARIANTS.ACTIVATED && euStore.passwordlessSignup

const showAppStoreLink = state !== STATES.IN_APP && !likelyHasPlayStore()
const showPlayStoreLink = state !== STATES.IN_APP && !likelyHasAppStore()

const step1State =
  variant === VARIANTS.ACTIVATED && !passwordless ? 'todo' : 'done'

const painAreaIcon = computed(() => {
  switch (selfTestStore.worstPainLocation) {
    case 'lower-back':
      return painLowerBack
    default:
      return painUpperBack
  }
})

const programNameKey = computed(() => {
  switch (selfTestStore.worstPainLocation) {
    case 'lower-back':
      return 'eu__contextual_download__program_name__lower_back'
    default:
      return 'eu__contextual_download__program_name__upper_back'
  }
})

const [step1Key, step2Key, step3Key] = (() => {
  if (passwordless) {
    return [
      'eu__contextual_download__activated_passwordless__step1',
      'eu__contextual_download__activated_passwordless__step2',
      'eu__contextual_download__activated_passwordless__step3',
    ]
  }
  switch (variant) {
    case VARIANTS.ACTIVATED:
      return [
        'eu__contextual_download__activated__step1',
        'eu__contextual_download__activated__step2',
        'eu__contextual_download__activated__step3',
      ]
    case VARIANTS.DOCTOR_OUTREACH:
      return [
        'eu__contextual_download__doctor__step1',
        'eu__contextual_download__doctor__step2',
        'eu__contextual_download__doctor__step3',
      ]
    default:
      return [
        'eu__contextual_download__prescription__step1',
        'eu__contextual_download__prescription__step2',
        'eu__contextual_download__prescription__step3',
      ]
  }
})()

onMounted(async () => {
  if (state === STATES.IN_APP) {
    showSpinner.value = true
    await ensureDeepLink()
    setTimeout(() => {
      showSpinner.value = false
    }, 2000)
    await redirect()
  }
})
</script>

<style lang="scss">
.contextual-download-link {
  &__spinner {
    width: 64px;
    height: 64px;
    margin: $kds-space-2x auto 0;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: $kds-space-3x;
  }

  &__layout {
    display: flex;
    flex-direction: column;
    gap: $kds-space-2x;
  }

  &__main {
    display: flex;
    flex-direction: column;
    gap: $kds-space-2x;
  }

  &__card {
    background-color: $kds-color-background-subtle;
    border-radius: $kds-shape-rounded-medium;
    padding: $kds-space-2x;
    display: flex;
    flex-direction: column;
    gap: $kds-space-2x;

    &--blue {
      background-color: $kds-color-background-blue-subtle;
    }
  }

  &__program {
    display: flex;
    align-items: center;
    gap: $kds-space-1_5x;
  }

  &__pain-icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    background-color: $kds-color-background-blue-strong;
  }

  &__store {
    display: flex;
    gap: $kds-space-1x;
    flex-wrap: wrap;

    &--column {
      flex-direction: column;
    }

    &-item {
      width: 143px;
      height: 48px;

      img {
        display: block;
        width: 100%;
        height: 100%;
      }
    }
  }

  &__step {
    overflow-wrap: break-word;
    margin: 0;
  }

  &__banner-text {
    margin: 0;
  }

  &__qr-code {
    width: 96px;
    height: 96px;
  }

  // Hidden on mobile, shown on desktop
  &__qr-banner {
    display: none;
  }

  // div.content inside ProgressStepsItem is a flex item with min-width: auto,
  // which prevents it from shrinking below the email's natural width.
  // 3-class specificity (0,3,0) beats the scoped rule (0,2,0).
  &__card .progress-steps-item .content {
    min-width: 0;
  }

  // ProgressStepsContainer masks the dashed line above/below the first/last
  // circles with background-color: white. Our card background is subtle gray,
  // so the white bars are visible. Fix: show the line above the first circle
  // (remove the mask) and blend the mask below the last circle with the card.
  &__card .progress-steps-container {
    .progress-steps-item:first-of-type::before {
      display: none;
    }

    .progress-steps-item:last-of-type::after {
      background-color: $kds-color-background-subtle;
    }
  }

  // The SVG icons have a white background rect that masks the dashed connector
  // line. Match the element background-color to the card so transparent corners
  // (outside the circle) blend in rather than showing white.
  &__card .progress-steps-item .progress {
    background-color: $kds-color-background-subtle;
  }

  &__card--blue .progress-steps-container {
    .progress-steps-item:last-of-type::after {
      background-color: $kds-color-background-blue-subtle;
    }
  }

  &__card--blue .progress-steps-item .progress {
    background-color: $kds-color-background-blue-subtle;
  }

  @include media-up(md) {
    &__layout {
      gap: $kds-space-3x;
    }

    &__main {
      flex-direction: row;
      align-items: stretch;
      gap: $kds-space-3x;
    }

    // Program card: right side, fixed 229px
    &__card--program {
      order: 2;
      flex: 0 0 229px;
      width: 229px;
    }

    // Steps card: left side, fills remaining space
    &__card--steps {
      order: 1;
      flex: 1 1 auto;
      min-width: 0;
    }

    // Mobile download card: hidden on desktop
    &__mobile-download {
      display: none;
    }

    // Desktop QR/download banner: full-width horizontal row
    &__qr-banner {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: $kds-space-2x;
    }

    &__banner-text {
      flex: 1;
    }

    &__qr-code {
      flex-shrink: 0;
    }

    &__store--column {
      flex-direction: column;
      flex-shrink: 0;
    }

    // ACTIVATED mobile store badges: hidden on desktop
    &__store--mobile-only {
      display: none;
    }
  }
}
</style>
